import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment,
  Snackbar, Stack, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import DataTable from '../shared/DataTable';
import { numFont } from '../../utils/fonts';

const API = '/api/vadmin/staff-api-keys/';

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' }) : '';
}

/** Superuser-only: keys for the read-only staff API (/api/staff/v1/). The plaintext key is
 * shown once, right after creation, and never again. */
export default function StaffApiKeysTab() {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(API);
      setKeys(data);
    } catch {
      setSnackbar({ open: true, message: t('vadmin.staffApiKeysTab.load_failed'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const closeCreate = () => {
    setCreateOpen(false);
    setName('');
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(API, { name: name.trim() });
      closeCreate();
      setCopied(false);
      setNewKey(data.api_key);
      await load();
    } catch (err) {
      notify(err?.response?.data?.name?.[0] || t('vadmin.staffApiKeysTab.create_failed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}${revokeTarget.id}/revoke/`);
      setRevokeTarget(null);
      notify(t('vadmin.staffApiKeysTab.revoked_ok'));
      await load();
    } catch {
      notify(t('vadmin.staffApiKeysTab.revoke_failed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(newKey);
    setCopied(true);
  };

  const columns = [
    { key: 'name', label: t('vadmin.staffApiKeysTab.name'), render: (r) => r.name, maxWidth: 260 },
    {
      key: 'key_prefix', label: t('vadmin.staffApiKeysTab.prefix'), width: '1%', noWrap: true,
      render: (r) => <Box component="code" sx={{ fontFamily: 'monospace' }}>{r.key_prefix}…</Box>,
    },
    { key: 'created_by', label: t('vadmin.staffApiKeysTab.created_by'), render: (r) => r.created_by || '', maxWidth: 220, hideBelow: 'md' },
    {
      key: 'created_at', label: t('vadmin.staffApiKeysTab.created_at'), width: '1%', noWrap: true, hideBelow: 'sm',
      render: (r) => <Box component="span" sx={numFont}>{formatDateTime(r.created_at)}</Box>,
    },
    {
      key: 'last_used_at', label: t('vadmin.staffApiKeysTab.last_used'), width: '1%', noWrap: true,
      render: (r) => <Box component="span" sx={numFont}>{r.last_used_at ? formatDateTime(r.last_used_at) : t('vadmin.staffApiKeysTab.never')}</Box>,
    },
    {
      key: 'status', label: t('vadmin.staffApiKeysTab.status'), width: '1%',
      render: (r) => (r.revoked_at
        ? <Chip size="small" label={t('vadmin.staffApiKeysTab.revoked')} data-testid={`staff-api-key-revoked-${r.id}`} />
        : <Chip size="small" color="success" label={t('vadmin.staffApiKeysTab.active')} />),
    },
    {
      key: 'actions', label: '', width: '1%', align: 'right',
      render: (r) => (!r.revoked_at && (
        <Button size="small" color="error" onClick={() => setRevokeTarget(r)} data-testid={`staff-api-key-revoke-${r.id}`}>
          {t('vadmin.staffApiKeysTab.revoke')}
        </Button>
      )),
    },
  ];

  return (
    <Box data-testid="vadmin-staff-api-keys-tab">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { sm: 'center' } }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {t('vadmin.staffApiKeysTab.intro')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<VpnKeyIcon />}
          onClick={() => setCreateOpen(true)}
          data-testid="staff-api-key-create"
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0 }}
        >
          {t('vadmin.staffApiKeysTab.create')}
        </Button>
      </Stack>

      <DataTable
        columns={columns}
        data={keys}
        loading={loading}
        emptyIcon={<VpnKeyIcon />}
        emptyTitle={t('vadmin.staffApiKeysTab.empty')}
        emptyDescription={t('vadmin.staffApiKeysTab.empty_help')}
      />

      <Dialog open={createOpen} onClose={closeCreate} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>{t('vadmin.staffApiKeysTab.create')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t('vadmin.staffApiKeysTab.name')}
            helperText={t('vadmin.staffApiKeysTab.name_help')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 100, 'data-testid': 'staff-api-key-name' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate} disabled={busy}>{t('Cancel')}</Button>
          <Button variant="contained" onClick={create} disabled={busy || !name.trim()} data-testid="staff-api-key-save">
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(newKey)} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>{t('vadmin.staffApiKeysTab.new_key_title')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>{t('vadmin.staffApiKeysTab.new_key_warning')}</Alert>
          <TextField
            fullWidth
            size="small"
            value={newKey || ''}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: '12px' },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? t('vadmin.staffApiKeysTab.copied') : t('vadmin.staffApiKeysTab.copy')}>
                    <IconButton size="small" onClick={copy}>
                      {copied ? <CheckIcon color="success" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            inputProps={{ 'data-testid': 'staff-api-key-plaintext' }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setNewKey(null)} data-testid="staff-api-key-saved-it">
            {t('vadmin.staffApiKeysTab.saved_it')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(revokeTarget)} onClose={() => setRevokeTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('vadmin.staffApiKeysTab.revoke_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('vadmin.staffApiKeysTab.revoke_body', { name: revokeTarget?.name || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={busy}>{t('Cancel')}</Button>
          <Button color="error" variant="contained" onClick={revoke} disabled={busy} data-testid="staff-api-key-revoke-confirm">
            {t('vadmin.staffApiKeysTab.revoke')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
